import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';
import { FaLine, FaWhatsapp } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay } from '../../primitives';

/**
 * Three guests writing in three languages, each answered in their own. The
 * conversations are deliberately not translated: that is the point.
 */
export default function GuestLanguages({
  brand,
  name,
}: {
  brand: string;
  name: string;
}) {
  const { t } = useLocale('bot-use-cases');

  return (
    <div className='grid gap-4 sm:grid-cols-2'>
      <div className={clsxm(styles.rv, 'sm:mt-10')} style={delay(100)}>
        <Thread
          lang='fr'
          header={
            <Header className='bg-[#075e54] dark:bg-[#1f2c34]'>
              <FaWhatsapp className='h-4 w-4 flex-none' />
              {name}
            </Header>
          }
          question='Le petit-déjeuner est-il compris ?'
          answer='Oui, il est servi de 7 h à 10 h 30, en terrasse.'
          note={t('Replied in French')}
        />
      </div>
      <div className={styles.rv} style={delay(250)}>
        <Thread
          lang='ja'
          header={
            <Header className='bg-[#06C755]'>
              <FaLine className='h-4 w-4 flex-none' />
              {name}
            </Header>
          }
          question='チェックインは何時からですか？'
          answer='15時からです。それより前でもお荷物をお預かりできます。'
          note={t('Replied in Japanese')}
        />
      </div>
      <div
        className={clsxm(styles.rv, 'sm:col-span-2 sm:mx-auto sm:w-1/2')}
        style={delay(400)}
      >
        <Thread
          lang='de'
          header={
            <Header className={brand}>
              <span className='flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/20 text-[11px] font-bold'>
                {name.charAt(0)}
              </span>
              {name}
            </Header>
          }
          question='Kann man bei euch Surfbretter leihen?'
          answer='Ja, ab 25 € pro Tag, direkt an der Rezeption.'
          note={t('Replied in German')}
        />
      </div>
    </div>
  );
}

function Header({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm(
        'flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-white',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Thread({
  lang,
  header,
  question,
  answer,
  note,
}: {
  lang: string;
  header: ReactNode;
  question: string;
  answer: string;
  note: string;
}) {
  return (
    <div className='overflow-hidden rounded-2xl bg-[#efeae2] shadow-[0_30px_70px_-25px_rgba(0,0,0,0.7)] ring-1 ring-black/5 dark:bg-[#0b141a] dark:ring-white/10'>
      {header}
      <div
        className='space-y-2 px-3 py-3 text-[13px] leading-snug'
        lang={lang}
        dir='ltr'
      >
        <p className='me-8 rounded-lg rounded-ss-none bg-white px-3 py-2 text-[#111b21] shadow-sm dark:bg-[#202c33] dark:text-[#e9edef]'>
          {question}
        </p>
        <p className='ms-8 rounded-lg rounded-se-none bg-[#d9fdd3] px-3 py-2 text-[#111b21] shadow-sm dark:bg-[#005c4b] dark:text-[#e9edef]'>
          {answer}
        </p>
      </div>
      <p className='mx-3 mb-3 rounded-full bg-white/70 px-3 py-1 text-center text-[11px] font-medium text-[#3b4a54] dark:bg-white/10 dark:text-[#c5d0d6]'>
        {note}
      </p>
    </div>
  );
}
